import { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';

/**
 * Separator for a member's tree position (its root→member chain of ids).
 *
 * Rows are addressed by POSITION, not by member id: a multi-parent member appears once under each
 * parent, so `id` is not unique across the tree. Within a single parent ids ARE unique (the server
 * builds the tree as a trie keyed by id per level), so a path is a stable, collision-free row key —
 * which is why this needs none of the occurrence-suffix machinery a materialized-tree feed would.
 */
export const MEMBER_PATH_DELIMITER = '|||';

/** The row key for a member at one tree position. */
export const memberPathKey = (path: readonly string[]): string => path.join(MEMBER_PATH_DELIMITER);

/** One rendered row of the member tree: a member at one position, with its depth. */
export interface MemberRow {
  /** Position key — see MEMBER_PATH_DELIMITER. Unique per row. */
  readonly key: string;
  readonly id: string;
  readonly label: string;
  readonly isLeaf: boolean;
  /** 0 for a root; drives the indent rails. */
  readonly depth: number;
  /** Root→member ids, inclusive. The member's TRUE place in the hierarchy. */
  readonly path: readonly string[];
  /**
   * The chain of ROW KEYS from this row's displayed top down to itself — the tree path a grid
   * renders from.
   *
   * Deliberately not `path`. A grid with tree data invents a placeholder group row for any path
   * prefix it was given no data for, so true paths would resurrect exactly the intermediate
   * consolidations `Keep + Leaves` exists to remove, and would re-level everything to hierarchy
   * depth instead of depth-from-the-kept-node. Row keys also cannot collide, so a multi-parent
   * member keeps one row per parent instead of the two merging.
   */
  readonly displayPath: readonly string[];
}

/**
 * Flatten the tree to the rows that are currently VISIBLE: a member shows when every ancestor
 * position is expanded. Depth-first, so row order is the hierarchy's own order.
 *
 * Everything starts collapsed, so this emits only the roots until the user expands — which is what
 * keeps a several-thousand-member hierarchy cheap to render without virtualization.
 */
export const visibleMemberRows = (members: readonly PnlHierarchyMember[], expanded: ReadonlySet<string>): MemberRow[] => {
  const rows: MemberRow[] = [];
  const walk = (nodes: readonly PnlHierarchyMember[], parentPath: readonly string[], parentKeys: readonly string[]): void => {
    for (const node of nodes) {
      const path = [...parentPath, node.id];
      const key = memberPathKey(path);
      const displayPath = [...parentKeys, key];
      rows.push({ key, id: node.id, label: node.label, isLeaf: node.is_leaf, depth: parentPath.length, path, displayPath });
      if (!node.is_leaf && expanded.has(key)) walk(node.children, path, displayPath);
    }
  };
  walk(members, [], []);
  return rows;
};

/** Total members counted as tree POSITIONS (a multi-parent member counts once per position). */
export const countMemberPositions = (members: readonly PnlHierarchyMember[]): number =>
  members.reduce((total, node) => total + 1 + countMemberPositions(node.children), 0);

// ─── Subset operations (TM1 subset-editor semantics) ─────────────────────────

/** One tree position, flattened for O(1) lookup by the subset operations. */
export interface MemberIndexEntry {
  readonly key: string;
  readonly id: string;
  readonly label: string;
  readonly isLeaf: boolean;
  readonly depth: number;
  readonly path: readonly string[];
  readonly childKeys: readonly string[];
  /** Depth-first position in the whole tree — the hierarchy's own order. */
  readonly order: number;
}

export type MemberIndex = ReadonlyMap<string, MemberIndexEntry>;

/** Flatten the tree to a position→entry index, recording depth-first order. */
export const indexMemberTree = (members: readonly PnlHierarchyMember[]): MemberIndex => {
  const index = new Map<string, MemberIndexEntry>();
  let order = 0;
  const walk = (nodes: readonly PnlHierarchyMember[], parentPath: readonly string[]): void => {
    for (const node of nodes) {
      const path = [...parentPath, node.id];
      const key = memberPathKey(path);
      const childKeys = node.children.map((child) => memberPathKey([...path, child.id]));
      index.set(key, {
        key,
        id: node.id,
        label: node.label,
        isLeaf: node.is_leaf,
        depth: parentPath.length,
        path,
        childKeys,
        order: order++
      });
      walk(node.children, path);
    }
  };
  walk(members, []);
  return index;
};

/**
 * Every position below `key`, all levels, consolidations included, in HIERARCHY order.
 *
 * Pre-order depth-first, not a stack pop: a LIFO walk returns the subtree reversed, which silently
 * reversed the leaf order everything downstream displays.
 */
export const descendantKeys = (index: MemberIndex, key: string): string[] => {
  const out: string[] = [];
  const walk = (parent: string): void => {
    for (const child of index.get(parent)?.childKeys ?? []) {
      out.push(child);
      walk(child);
    }
  };
  walk(key);
  return out;
};

/**
 * The leaf positions under `key`.
 *
 * A leaf is its OWN leaf set, so `Keep Leaves` on a leaf keeps it rather than deleting it — the
 * alternative silently empties the subset when someone right-clicks a bottom-level member.
 */
export const leafKeysUnder = (index: MemberIndex, key: string): string[] => {
  const entry = index.get(key);
  if (!entry) return [];
  if (entry.isLeaf) return [key];
  return descendantKeys(index, key).filter((candidate) => index.get(candidate)?.isLeaf);
};

/**
 * The subset operations, as named in the TM1 subset editor.
 *
 * Each one keeps the clicked node as the new TOP of the pane and fixes how much of its subtree is
 * shown; the displayed level is therefore relative to that node, not the hierarchy root. That is
 * the "levels auto-update" behaviour: Keep + Leaves on Contribution Profit puts it at level 0 with
 * its leaves at level 1, however deep they really sit.
 *
 *   * `keep`             — the node, subtree intact but COLLAPSED (expandable).
 *   * `keepChildren`     — the node expanded one level; each child still holds its own rollup.
 *   * `keepDescendants`  — the node expanded all the way down to its leaves.
 *   * `keepLeaves`       — "Keep + Leaves": the node plus its leaves, with NO intermediate
 *                        consolidations between them.
 *   * `leavesOnly`       — "Keep Leaves": its leaves alone; the node itself is not shown. The
 *                        internal key stays `leavesOnly` precisely because the two USER-FACING
 *                        names differ by one plus sign and are trivial to confuse in code.
 */
export type MemberSubsetOp = 'keep' | 'keepChildren' | 'keepDescendants' | 'keepLeaves' | 'leavesOnly';

export const MEMBER_SUBSET_OP_LABELS: Readonly<Record<MemberSubsetOp, string>> = {
  keep: 'Keep',
  keepChildren: 'Keep + Children',
  keepDescendants: 'Keep + Descendants',
  keepLeaves: 'Keep + Leaves',
  leavesOnly: 'Keep Leaves'
};

/** A built subset: which nodes anchor the pane, and how much of each subtree it shows. */
export interface MemberSubsetScope {
  /** Position keys, hierarchy order, none a descendant of another. */
  readonly anchors: readonly string[];
  readonly op: MemberSubsetOp;
}

/** Drop anchors that sit under another anchor — their rows would be emitted twice. */
const topmost = (index: MemberIndex, anchors: readonly string[]): string[] => {
  const kept = anchors.filter((anchor) => index.has(anchor));
  return kept
    .filter((anchor) => {
      const path = index.get(anchor)?.path ?? [];
      return !kept.some((other) => {
        if (other === anchor) return false;
        const otherPath = index.get(other)?.path ?? [];
        return otherPath.length < path.length && memberPathKey(path.slice(0, otherPath.length)) === other;
      });
    })
    .sort((left, right) => (index.get(left)?.order ?? 0) - (index.get(right)?.order ?? 0));
};

export const buildMemberSubsetScope = (index: MemberIndex, op: MemberSubsetOp, selected: readonly string[]): MemberSubsetScope => ({
  anchors: topmost(index, selected),
  op
});

/**
 * The expansion each operation opens with. Expansion is what distinguishes `keep` from
 * `keepDescendants` — they scope the same subtree, one closed and one open — so it has to be seeded
 * by the operation rather than left at whatever the user had expanded while browsing.
 */
export const seedExpansion = (index: MemberIndex, scope: MemberSubsetScope): Set<string> => {
  const expanded = new Set<string>();
  if (scope.op === 'keep' || scope.op === 'leavesOnly') return expanded;
  for (const anchor of scope.anchors) {
    expanded.add(anchor);
    // Descendants opens the whole subtree; children and leaves open only the anchor.
    if (scope.op === 'keepDescendants') for (const key of descendantKeys(index, anchor)) expanded.add(key);
  }
  return expanded;
};

/**
 * The rows a scope shows, with depth measured FROM THE ANCHOR (anchors at 0).
 *
 * These rows are also the subset's membership: what you see is what the report gets, so collapsing
 * a node removes it from the report exactly as it removes it from the pane. That keeps the editor a
 * true preview instead of a second, invisible source of truth.
 */
export const scopeRows = (index: MemberIndex, scope: MemberSubsetScope, expanded: ReadonlySet<string>): MemberRow[] => {
  const rows: MemberRow[] = [];
  const seen = new Set<string>();
  const push = (key: string, depth: number, isLeaf: boolean, parentKeys: readonly string[]): void => {
    const entry = index.get(key);
    if (!entry || seen.has(key)) return;
    seen.add(key);
    rows.push({ key, id: entry.id, label: entry.label, isLeaf, depth, path: entry.path, displayPath: [...parentKeys, key] });
  };

  for (const anchor of scope.anchors) {
    const anchorEntry = index.get(anchor);
    if (!anchorEntry) continue;

    if (scope.op === 'leavesOnly') {
      // A flat list of leaves; the anchor is deliberately absent, so each leaf is its own top.
      for (const leafKey of leafKeysUnder(index, anchor)) push(leafKey, 0, true, []);
      continue;
    }

    if (scope.op === 'keepLeaves') {
      // Intermediate consolidations are skipped, so every leaf sits one level under the anchor
      // however deep it really is.
      const leaves = leafKeysUnder(index, anchor);
      push(anchor, 0, anchorEntry.isLeaf || leaves.length === 0, []);
      // Every leaf hangs directly off the anchor in the DISPLAY path, which is what collapses the
      // intermediate consolidations away however deep they really sit.
      if (expanded.has(anchor)) for (const leafKey of leaves) if (leafKey !== anchor) push(leafKey, 1, true, [anchor]);
      continue;
    }

    // keep / keepChildren / keepDescendants all show the REAL subtree; they differ only in how much
    // of it starts open, so one walk serves all three.
    const walk = (key: string, depth: number, parentKeys: readonly string[]): void => {
      const entry = index.get(key);
      if (!entry) return;
      push(key, depth, entry.isLeaf, parentKeys);
      if (!entry.isLeaf && expanded.has(key)) for (const child of entry.childKeys) walk(child, depth + 1, [...parentKeys, key]);
    };
    walk(anchor, 0, []);
  }
  return rows;
};

// ─── Search ──────────────────────────────────────────────────────────────────

/** Every position, for the "reveal everything" pass search needs. */
export const allPositionKeys = (index: MemberIndex): ReadonlySet<string> => new Set(index.keys());

/** What a search leaves on screen: the surviving rows, and which of them start open. */
export interface MemberSearchResult {
  readonly rows: MemberRow[];
  /** Ancestors of the hits — opened so the hit is visible. Hits themselves stay closed. */
  readonly expand: ReadonlySet<string>;
}

/**
 * Narrow rows to the members matching `term`, keeping
 *   * each match's ROLLUP (the ancestors above it), so a hit is shown in the branch it belongs to;
 *   * each match's own SUBTREE, so a hit is still expandable — filtering a match's children away
 *     stripped its expander and made it impossible to see what rolls into it.
 * Non-matching branches disappear entirely.
 *
 * Only the ancestors are opened. The matches keep their subtrees collapsed, so a search for a
 * high-level rollup does not dump thousands of descendants on screen.
 *
 * Ancestors and descendants are both resolved through `displayPath`, never the hierarchy `path`.
 * Those coincide while browsing but not inside a built scope, where the display chain is whatever the
 * operation decided (`Keep + Leaves` hangs a leaf straight off its anchor) — using hierarchy paths
 * there kept rows the grid has no node for and dropped ones it does, leaving it to invent
 * placeholder rows.
 *
 * Matches on label and on member id, because ids are what analysts paste in and labels collide.
 */
export const searchMemberRows = (rows: readonly MemberRow[], term: string): MemberSearchResult => {
  const needle = term.trim().toLowerCase();
  if (needle.length === 0) return { rows: [...rows], expand: new Set<string>() };

  const matched = new Set<string>();
  for (const row of rows) {
    if (row.label.toLowerCase().includes(needle) || row.id.toLowerCase().includes(needle)) matched.add(row.key);
  }

  const keep = new Set<string>(matched);
  const expand = new Set<string>();
  for (const row of rows) {
    if (matched.has(row.key)) {
      for (const segment of row.displayPath.slice(0, -1)) {
        keep.add(segment);
        expand.add(segment);
      }
      continue;
    }
    // A descendant of a hit: keep it so the hit can be expanded into.
    if (row.displayPath.some((segment) => matched.has(segment))) keep.add(row.key);
  }
  return { rows: rows.filter((row) => keep.has(row.key)), expand };
};

// ─── Removal ─────────────────────────────────────────────────────────────────

/**
 * Drop `excluded` rows, PROMOTING anything that survives beneath them.
 *
 * The display path is a chain of row keys, so removing an excluded key from every path re-parents
 * orphans onto their nearest surviving ancestor for free. That is what keeps `Remove Elements` and
 * `Remove Elements + Descendants` distinct: the first removes one member and lifts its children a
 * level, the second (caller passes the subtree) removes the branch.
 *
 * It also has to work this way: a grid with tree data invents a placeholder row for any path prefix
 * with no data behind it, so leaving an excluded key in a surviving row's path would resurrect the
 * member that was just removed.
 */
export const applyExclusions = (rows: readonly MemberRow[], excluded: ReadonlySet<string>): MemberRow[] => {
  if (excluded.size === 0) return [...rows];
  return rows
    .filter((row) => !excluded.has(row.key))
    .map((row) => {
      const displayPath = row.displayPath.filter((segment) => !excluded.has(segment));
      return displayPath.length === row.displayPath.length ? row : { ...row, displayPath, depth: displayPath.length - 1 };
    });
};
